import React from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
} from '@tonic/shared/components/ui';
import { DashboardChart } from '@tonic/shared/components/ui';
import { Calendar, Leaf } from 'lucide-react';

// Mock data for recycling analytics
const mockRecycleData = {
  recycleByStatus: [
    { name: 'Completed', value: 245 },
    { name: 'Processing', value: 84 },
    { name: 'Pending', value: 42 },
    { name: 'Rejected', value: 15 },
  ],
  recycleByType: [
    { name: 'Furniture', value: 156 },
    { name: 'Electronics', value: 89 },
    { name: 'Textiles', value: 76 },
    { name: 'Packaging', value: 65 },
  ],
  monthlyTrends: [
    { date: '2024-01', items: 82, weight: 2450 },
    { date: '2024-02', items: 95, weight: 2800 },
    { date: '2024-03', items: 115, weight: 3200 },
  ],
  materialRecovery: [
    { name: 'Wood', recovered: 85, target: 90 },
    { name: 'Metal', recovered: 92, target: 95 },
    { name: 'Plastic', recovered: 78, target: 85 },
    { name: 'Textile', recovered: 72, target: 80 },
  ],
  environmentalImpact: [
    { date: '2024-01', carbonSaved: 12.5, waterSaved: 2500 },
    { date: '2024-02', carbonSaved: 14.2, waterSaved: 2800 },
    { date: '2024-03', carbonSaved: 15.8, waterSaved: 3100 },
  ],
  recyclingEfficiency: [
    { range: '90-100%', count: 145 },
    { range: '80-90%', count: 98 },
    { range: '70-80%', count: 54 },
    { range: '<70%', count: 22 },
  ],
};

const timeRanges = [
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
  { value: '12m', label: 'Last 12 Months' },
];

export function RecycleAnalyticsPage() {
  const [timeRange, setTimeRange] = React.useState('30d');

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Recycling Analytics</h1>
          <p className="text-sm text-muted-foreground">
            Overview of recycling operations and environmental impact
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-muted-foreground" />
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {timeRanges.map((range) => (
                <SelectItem key={range.value} value={range.value}>
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Items by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="pie"
              data={mockRecycleData.recycleByStatus}
              dataKey="value"
              xAxisKey="name"
              height={300}
              colors={['#10B981', '#6366F1', '#F59E0B', '#EF4444']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Items by Type</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="pie"
              data={mockRecycleData.recycleByType}
              dataKey="value"
              xAxisKey="name"
              height={300}
              colors={['#6366F1', '#10B981', '#F59E0B', '#3B82F6']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Monthly Recycling Volume</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="line"
              data={mockRecycleData.monthlyTrends}
              dataKey="items"
              xAxisKey="date"
              height={300}
              colors={['#6366F1']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Material Weight Processed</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="line"
              data={mockRecycleData.monthlyTrends}
              dataKey="weight"
              xAxisKey="date"
              height={300}
              colors={['#10B981']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Material Recovery Rates</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="bar"
              data={mockRecycleData.materialRecovery}
              dataKey="recovered"
              xAxisKey="name"
              height={300}
              colors={['#F59E0B']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recycling Efficiency</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="bar"
              data={mockRecycleData.recyclingEfficiency}
              dataKey="count"
              xAxisKey="range"
              height={300}
              colors={['#6366F1']}
            />
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Environmental Impact</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <DashboardChart
                type="line"
                data={mockRecycleData.environmentalImpact}
                dataKey="carbonSaved"
                xAxisKey="date"
                height={300}
                colors={['#10B981']}
              />
              <DashboardChart
                type="line"
                data={mockRecycleData.environmentalImpact}
                dataKey="waterSaved"
                xAxisKey="date"
                height={300}
                colors={['#3B82F6']}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
